import {
  Button,
  Checkbox,
  Divider,
  makeStyles,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Tooltip,
} from "@fluentui/react-components";
import { useId } from "@fluentui/react-hooks";
import {
  ArrowSyncCircleFilled,
  ArrowSyncCircleRegular,
  bundleIcon,
} from "@fluentui/react-icons";
import { $gray200, $sky10, $sky60 } from "../../../cssjs/colors";
import { FlightCardTypes } from "../../../enum/flights/FlightEnum";
import { FlightLegTypeEnum } from "../../../enum/FlightsEnum";
import { flightTableViewTypes } from "../../../utils/flights/FlightConstants";
import { displayAllFlightsView } from "../../../utils/flights/FlightDetailHelperFunctions";
import { default as HideIcon } from "./../../../assests/flights/hide.svg";
import { default as CustomColumnDropdown } from "./CustomColumnDropdown";

const SyncIcon = bundleIcon(ArrowSyncCircleFilled, ArrowSyncCircleRegular);

interface ILegtypeCount {
  all: number;
  arrival: number;
  departure: number;
  turnaround: number;
}

export interface IFlightDetailsHeaderProps {
  viewType: FlightCardTypes;
  isScheduleTimeChecked: boolean;
  selectedButton: string | null;
  legTypeCount: ILegtypeCount;
  isScreenCalloutVisible: any;
  toggleIsScreenCalloutVisible: any;
  transformColumns: any;
  handleButtonClick: (buttonId: FlightLegTypeEnum) => void;
  handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleColumnCheckboxChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onResetHandler: (_event: React.MouseEvent<HTMLButtonElement>) => void;
  onDefaultHandler: (_event: React.MouseEvent<HTMLButtonElement>) => void;
  onApplyHandler: (_event: React.MouseEvent<HTMLButtonElement>) => void;
  setRefreshTable: (value: Date) => void;
}

export default function FlightDetailsHeader({
  viewType,
  selectedButton,
  isScheduleTimeChecked,
  legTypeCount,
  handleButtonClick,
  handleCheckboxChange,
  transformColumns,
  handleColumnCheckboxChange,
  onResetHandler,
  onDefaultHandler,
  onApplyHandler,
  isScreenCalloutVisible,
  toggleIsScreenCalloutVisible,
  setRefreshTable,
}: IFlightDetailsHeaderProps) {
  const screenButtonId = useId("screen-button");

  const classes = useStyles();
  const { label } = flightTableViewTypes[viewType] || { label: "" };
  const showAllButton = displayAllFlightsView(viewType);
  const allCount = legTypeCount.all;
  const arrivalCount = legTypeCount.arrival + legTypeCount.turnaround;
  const departureCount = legTypeCount.departure + legTypeCount.turnaround;

  return (
    <header className="amd-container-hspacebtwn pa-sm-3 ai-center pt-sm-4">
      <div className="d-flex">
        <span className="fs-20 fw-500 pr-sm-5 as-center">{label}</span>

        {showAllButton && (
          <Button
            className={mergeClasses(
              selectedButton === FlightLegTypeEnum.All
                ? classes.selectedButton
                : classes.unselecedButton,
              classes.button
            )}
            onClick={() => handleButtonClick(FlightLegTypeEnum.All)}
          >
            All ({allCount})
          </Button>
        )}

        <Button
          className={mergeClasses(
            selectedButton === FlightLegTypeEnum.Turnaround
              ? classes.selectedButton
              : classes.unselecedButton,
            classes.button
          )}
          onClick={() => handleButtonClick(FlightLegTypeEnum.Turnaround)}
        >
          Turnaround
        </Button>

        <Button
          className={mergeClasses(
            selectedButton === FlightLegTypeEnum.Arrival
              ? classes.selectedButton
              : classes.unselecedButton,
            classes.button
          )}
          disabled={viewType === FlightCardTypes.REGULATED_FLIGHT}
          onClick={() => handleButtonClick(FlightLegTypeEnum.Arrival)}
        >
          Arrival ({arrivalCount})
        </Button>

        <Button
          className={mergeClasses(
            selectedButton === FlightLegTypeEnum.Departure
              ? classes.selectedButton
              : classes.unselecedButton,
            classes.button
          )}
          onClick={() => handleButtonClick(FlightLegTypeEnum.Departure)}
        >
          Departure ({departureCount})
        </Button>
      </div>

      <div className="d-flex ai-center">
        <Checkbox
          id="scheduleTime"
          label="Schedule Time"
          checked={isScheduleTimeChecked}
          onChange={handleCheckboxChange}
        />

        <Popover
          open={isScreenCalloutVisible}
          onOpenChange={toggleIsScreenCalloutVisible}
          positioning="below-end"
        >
          <PopoverTrigger>
            <Tooltip content="Customize columns" relationship="label">
              <Button
                onClick={() => toggleIsScreenCalloutVisible}
                icon={
                  <img
                    src={HideIcon}
                    id={screenButtonId}
                    alt="Customize columns config button"
                    style={{ cursor: "pointer" }}
                  />
                }
                iconPosition="after"
              />
            </Tooltip>
          </PopoverTrigger>

          <PopoverSurface>
            <CustomColumnDropdown
              transformColumns={transformColumns}
              handleColumnCheckboxChange={handleColumnCheckboxChange}
              onResetHandler={onResetHandler}
              onDefaultHandler={onDefaultHandler}
              onApplyHandler={onApplyHandler}
            />
          </PopoverSurface>
        </Popover>
        <Divider
          className={mergeClasses(classes.divider, "px-sm-1")}
          vertical
          appearance="strong"
        />
        <Tooltip content="Refresh table" relationship="label">
          <Button
            appearance="transparent"
            aria-label="Refresh table"
            onClick={() => setRefreshTable(new Date())}
            className={classes.refreshButton}
            data-testid="refresh-button"
          >
            <SyncIcon fontSize={24} />
          </Button>
        </Tooltip>
      </div>
    </header>
  );
}

const useStyles = makeStyles({
  button: {
    marginRight: "10px",
    borderRadius: "22px",
    fontSize: "12px !important",
  },
  unselecedButton: {
    border: `2px solid ${$gray200}`,
    "&:hover": {
      border: `2px solid ${$sky60}`,
      backgroundColor: $sky10,
    },
  },
  selectedButton: {
    backgroundColor: $sky60,
    color: "white",
    border: "2px solid #0a4ba5",
    "&:hover": {
      border: "2px solid #0a4ba5",
      backgroundColor: $sky60,
      color: "white",
    },
  },
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  buttonGroup: {
    display: "flex",
    gap: "10px",
  },
  refreshButton: {
    minWidth: "20px",
    padding: "0px",
  },
  divider: {
    minHeight: "32px",
    padding: "0px 8px",
  },
});
